import Vue from "vue";
import {
  SelectOption,
  Grade,
  Subject,
  SelectOptionKey
} from "resources/assets/types";
import axios from "axios";

const NEW_SUBJECT_KEY = "other";

const certifiedChoices: SelectOption[] = [
  {
    key: "certified",
    value: 1,
    label: "I'm Certified"
  },
  {
    key: "not certified",
    value: 0,
    label: "Not Certified"
  }
];

export const PageComponent = Vue.extend({
  props: {
    initialIsCertified: {
      type: Number,
      required: false
    },
    initialGrades: {
      type: Array as () => SelectOptionKey[],
      required: false,
      default: (): SelectOptionKey[] => []
    },
    initialSubjects: {
      type: Array as () => SelectOptionKey[],
      required: false,
      default: (): SelectOptionKey[] => []
    },
    initialCertificationType: {
      type: String,
      required: false
    },
    initialNewSubject: {
      type: String,
      required: false
    }
  },

  mounted() {
    this.loadGradesAndSubjects();
  },

  data() {
    const initialSubjects = this.initialSubjects;

    if (this.initialNewSubject) {
      initialSubjects.push(NEW_SUBJECT_KEY);
    }

    return {
      certifiedChoices,
      grades: [] as Grade[],
      subjects: [] as Subject[],
      isCertified: this.initialIsCertified,
      certificationType: this.initialCertificationType,
      selectedGrades: this.initialGrades,
      selectedSubjects: initialSubjects,
      newSubject: this.initialNewSubject
    };
  },

  computed: {
    isValid(): boolean {
      if (this.isCertified === null) {
        return false;
      }

      if (this.isCertified) {
        if (
          !this.certificationType.trim().length ||
          !this.selectedGrades.length
        ) {
          return false;
        }
      }

      if (!this.selectedSubjects.length) {
        return false;
      }

      return true;
    },

    isCertifiedKey(): SelectOptionKey | null {
      return (
        certifiedChoices.find(c => c.value === this.isCertified)?.key || null
      );
    },

    gradeChoices(): SelectOption[] {
      return this.grades.map(grade => ({
        key: grade.id,
        value: grade.id,
        label: grade.description
      }));
    },

    gradeKeys(): SelectOptionKey[] {
      return this.gradeChoices
        .filter(c => this.selectedGrades.includes(c.key))
        .map(c => c.key);
    },

    subjectChoices(): SelectOption[] {
      const subjects: SelectOption[] = this.subjects.map(subject => ({
        key: subject.id,
        value: subject.id,
        label: subject.description
      }));

      subjects.push({
        key: NEW_SUBJECT_KEY,
        value: null,
        label: "Other"
      });

      return subjects;
    },

    subjectKeys(): SelectOptionKey[] {
      return this.subjectChoices
        .filter(c => this.selectedSubjects.includes(c.key))
        .map(c => c.key);
    },

    isRequestingNewSubject(): boolean {
      return this.selectedSubjects.includes(NEW_SUBJECT_KEY);
    }
  },

  methods: {
    updateIsCertified(key: SelectOptionKey) {
      this.isCertified =
        certifiedChoices.find(choice => choice.key === key)?.value || 0;
    },

    updateCertificationType(value: string) {
      this.certificationType = value;
    },

    updateGrades(keys: SelectOptionKey[]) {
      this.selectedGrades = keys;
    },

    updateSubjects(keys: SelectOptionKey[]) {
      this.selectedSubjects = keys;
    },

    updateNewSubject(value: string) {
      this.newSubject = value;
    },

    async loadGradesAndSubjects() {
      const [grades, subjects] = await Promise.all([
        axios.get("/ajax/grades"),
        axios.get("/ajax/subjects")
      ]);

      this.grades = grades?.data?.data || [];
      this.subjects = subjects?.data?.data || [];
    }
  }
});
